import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "./../../config/api";
import AdsSquare from "../../Admin/Pages/AdsSquare";

export default function Login({ isOpen, onClose, openRegister }) {
  const navigate = useNavigate();

  /*
  |--------------------------------------------------------------------------
  | LOGIN STATE
  |--------------------------------------------------------------------------
  */

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    country_code: "+91",
    new_password: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | FORGOT PASSWORD STATE
  |--------------------------------------------------------------------------
  */

  const [showForgot, setShowForgot] = useState(false);

  const [forgotIdentifier, setForgotIdentifier] = useState("");

  const [forgotOtp, setForgotOtp] = useState("");

  const [forgotStep, setForgotStep] = useState(1);

  const [forgotEmail, setForgotEmail] = useState("");

  const [forgotLoading, setForgotLoading] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  /*
  |--------------------------------------------------------------------------
  | OTP TIMER
  |--------------------------------------------------------------------------
  */

  const [otpTimer, setOtpTimer] = useState(0);

  const [otpSending, setOtpSending] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | OTP COUNTDOWN
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (otpTimer <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpTimer]);

  /*
  |--------------------------------------------------------------------------
  | FORMAT TIMER
  |--------------------------------------------------------------------------
  */

  const formatOtpTimer = () => {
    const minutes = Math.floor(otpTimer / 60);
    const seconds = otpTimer % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  /*
  |--------------------------------------------------------------------------
  | LOGIN VALIDATION
  |--------------------------------------------------------------------------
  */

  const validateForm = () => {
    const newErrors = {};

    const value = formData.identifier.trim();

    if (!value) {
      newErrors.identifier = "Email or Phone number is required";
    } else {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      if (!isEmail) {
        const digits = value.replace(/\D/g, "");

        if (digits.length < 8 || digits.length > 15) {
          newErrors.identifier = "Enter valid phone number (8-15 digits)";
        }
      }
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /*
  |--------------------------------------------------------------------------
  | HANDLE LOGIN INPUT
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | LOGIN
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const identifierToSend = formData.identifier.trim();

      const payload = {
        identifier: identifierToSend,
        password: formData.password,
      };

      const res = await fetch(API.LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        alert(`⚠️ ${data.message || "Invalid credentials"}`);

        setFormData((prev) => ({
          ...prev,
          password: "",
        }));

        return;
      }
      if (rememberMe) {
        localStorage.setItem(
          "rememberedLogin",
          JSON.stringify({
            identifier: identifierToSend,
            password: formData.password,
          }),
        );
      } else {
        localStorage.removeItem("rememberedLogin");
      }
      /*
      |--------------------------------------------------------------------------
      | BLOCKED USER
      |--------------------------------------------------------------------------
      */

      if (data.user?.status?.toLowerCase() === "blocked") {
        sessionStorage.removeItem("user");

        alert("🚫 Your account has been blocked.");

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | STORE USER DATA
      |--------------------------------------------------------------------------
      */

      const userData = {
        ...data.user,

        provider_options: Array.isArray(data.user.provider_options)
          ? data.user.provider_options
          : data.user.provider_options
            ? JSON.parse(data.user.provider_options || "[]")
            : [],

        supplier_types: Array.isArray(data.user.supplier_types)
          ? data.user.supplier_types
          : data.user.supplier_types
            ? JSON.parse(data.user.supplier_types || "[]")
            : [],

        design_types: Array.isArray(data.user.design_types)
          ? data.user.design_types
          : data.user.design_types
            ? JSON.parse(data.user.design_types || "[]")
            : [],
      };

      sessionStorage.setItem("user", JSON.stringify(userData));

      sessionStorage.setItem("userId", data.user.id);

      window.dispatchEvent(new Event("userLogin"));

      handleClose();

      /*
      |--------------------------------------------------------------------------
      | NAVIGATION
      |--------------------------------------------------------------------------
      */

      if (data.user.role === "admin") {
        window.open("/admin/dashboard", "_blank");
      } else if (data.user.account_type === "provider") {
        window.open("/user/dashboard", "_blank");
      } else {
        window.open("/", "_blank");
      }
    } catch (error) {
      console.error("Login error:", error);

      alert("Server error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | RESET FORM
  |--------------------------------------------------------------------------
  */

  const resetForm = () => {
    setFormData({
      identifier: "",
      password: "",
      country_code: "+91",
      new_password: "",
    });

    setErrors({});

    setIsSubmitting(false);

    setShowPassword(false);

    setShowForgot(false);

    setForgotIdentifier("");

    setForgotOtp("");

    setForgotStep(1);

    setForgotEmail("");

    setForgotLoading(false);

    setShowNewPassword(false);

    setOtpTimer(0);

    setOtpSending(false);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE LOGIN
  |--------------------------------------------------------------------------
  */

  const handleClose = () => {
    resetForm();

    onClose();
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE FORGOT PASSWORD
  |--------------------------------------------------------------------------
  */

  const closeForgotPassword = () => {
    setShowForgot(false);

    setForgotStep(1);

    setForgotIdentifier("");

    setForgotOtp("");

    setForgotEmail("");

    setForgotLoading(false);

    setShowNewPassword(false);

    setOtpTimer(0);

    setOtpSending(false);
  };

  /*
  |--------------------------------------------------------------------------
  | SEND / VERIFY PASSWORD RESET
  |--------------------------------------------------------------------------
  */

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    /*
    |--------------------------------------------------------------------------
    | STEP 1 - SEND OTP
    |--------------------------------------------------------------------------
    */

    if (forgotStep === 1) {
      const identifier = forgotIdentifier.trim();

      if (!identifier) {
        alert("Enter registered email or mobile number");

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | BASIC EMAIL / MOBILE VALIDATION
      |--------------------------------------------------------------------------
      */

      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

      if (!isEmail) {
        const digits = identifier.replace(/\D/g, "");

        if (digits.length < 8 || digits.length > 15) {
          alert("Enter valid email or mobile number");

          return;
        }
      }

      setForgotLoading(true);

      try {
        const res = await fetch(API.SEND_PASSWORD_RESET_OTP, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            identifier: identifier,
          }),
        });

        const data = await res.json();

        if (data.success) {
          /*
          |--------------------------------------------------------------------------
          | MOVE TO OTP STEP
          |--------------------------------------------------------------------------
          */

          setForgotStep(2);

          setForgotEmail(data.email || "");

          /*
          |--------------------------------------------------------------------------
          | START 60 SECOND RESEND TIMER
          |--------------------------------------------------------------------------
          */

          setOtpTimer(600);

          alert(
            `OTP sent successfully to ${data.email || "your registered email"}`,
          );
        } else {
          alert(data.message || "Unable to send OTP");
        }
      } catch (error) {
        console.error("Send OTP error:", error);

        alert("Server error");
      } finally {
        setForgotLoading(false);
      }

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | STEP 2 - VERIFY OTP + UPDATE PASSWORD
    |--------------------------------------------------------------------------
    */

    if (forgotStep === 2) {
      if (!forgotOtp.trim()) {
        alert("Enter OTP");

        return;
      }

      if (!/^\d{6}$/.test(forgotOtp.trim())) {
        alert("Enter valid 6 digit OTP");

        return;
      }

      if (!(formData.new_password || "").trim()) {
        alert("Enter new password");

        return;
      }

      if (formData.new_password.length < 6) {
        alert("Password must be at least 6 characters");

        return;
      }

      setForgotLoading(true);

      try {
        const res = await fetch(API.UPDATE_PASSWORD, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            identifier: forgotIdentifier.trim(),

            otp: forgotOtp.trim(),

            new_password: formData.new_password,
          }),
        });

        const data = await res.json();

        if (data.success) {
          alert("Password updated successfully. Please login.");

          /*
          |--------------------------------------------------------------------------
          | RETURN TO LOGIN
          |--------------------------------------------------------------------------
          */

          setShowForgot(false);

          setForgotStep(1);

          setForgotIdentifier("");

          setForgotOtp("");

          setForgotEmail("");

          setFormData((prev) => ({
            ...prev,
            new_password: "",
            password: "",
          }));

          setShowNewPassword(false);

          setOtpTimer(0);
        } else {
          alert(data.message || "Failed to update password");
        }
      } catch (error) {
        console.error("Reset password error:", error);

        alert("Server error");
      } finally {
        setForgotLoading(false);
      }
    }
  };

  /*
  |--------------------------------------------------------------------------
  | RESEND OTP
  |--------------------------------------------------------------------------
  */

  const handleResendOtp = async () => {
    if (otpTimer > 0 || otpSending) {
      return;
    }

    const identifier = forgotIdentifier.trim();

    if (!identifier) {
      alert("Enter registered email or mobile number");

      return;
    }

    setOtpSending(true);

    try {
      const res = await fetch(API.SEND_PASSWORD_RESET_OTP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: identifier,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setForgotEmail(data.email || "");

        setForgotOtp("");

        /*
        |--------------------------------------------------------------------------
        | RESTART 60 SECOND TIMER
        |--------------------------------------------------------------------------
        */

        setOtpTimer(600);

        alert(
          `New OTP sent successfully to ${
            data.email || "your registered email"
          }`,
        );
      } else {
        alert(data.message || "Unable to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);

      alert("Server error");
    } finally {
      setOtpSending(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CHECK USER STATUS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const checkUserStatus = async () => {
      const savedUser = JSON.parse(sessionStorage.getItem("user"));

      if (savedUser && savedUser.id) {
        try {
          const res = await fetch(`${API.GET_USER_STATUS}?id=${savedUser.id}`);

          const data = await res.json();

          if (data.status?.toLowerCase() === "blocked") {
            sessionStorage.removeItem("user");

            alert("Account blocked");

            navigate("/");
          }
        } catch (error) {
          console.log(error);
        }
      }
    };

    checkUserStatus();

    const interval = setInterval(checkUserStatus, 300000);

    return () => clearInterval(interval);
  }, [navigate]);
  useEffect(() => {
    const savedLogin = localStorage.getItem("rememberedLogin");

    if (savedLogin) {
      try {
        const loginData = JSON.parse(savedLogin);

        setFormData((prev) => ({
          ...prev,
          identifier: loginData.identifier || "",
          password: loginData.password || "",
        }));

        setRememberMe(true);
      } catch (error) {
        localStorage.removeItem("rememberedLogin");
      }
    }
  }, []);
  /*
  |--------------------------------------------------------------------------
  | DON'T SHOW MODAL
  |--------------------------------------------------------------------------
  */

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* =========================================================
          LOGIN MODAL
      ========================================================= */}

      <div className="login-popup-overlay">
        <div className="login-popup-card login-popup-layout p-1">
          {/* LEFT ADVERTISEMENT */}

          <div className="login-popup-ad">
            <AdsSquare />
          </div>

          {/* RIGHT CONTENT */}

          <div className="login-popup-content">
            <button className="login-popup-close" onClick={handleClose}>
              ✕
            </button>

            <h2 className="login-popup-title">Sign In</h2>

            <form onSubmit={handleSubmit}>
              {/* IDENTIFIER */}

              <div className="login-popup-form-group">
                <label>Email or Mobile Number</label>

                <input
                  type="text"
                  name="identifier"
                  className={`login-popup-input ${
                    errors.identifier ? "is-invalid" : ""
                  }`}
                  value={formData.identifier}
                  onChange={handleChange}
                  placeholder="Enter email or mobile number"
                  autoComplete="username"
                />

                {errors.identifier && (
                  <div className="login-popup-error">{errors.identifier}</div>
                )}
              </div>

              {/* PASSWORD */}

              <div className="login-popup-form-group">
                <label>Password</label>

                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className={`login-popup-input ${
                      errors.password ? "is-invalid" : ""
                    }`}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    autoComplete="current-password"
                  />

                  <span
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={`bi ${
                        showPassword ? "bi-eye-slash" : "bi-eye"
                      }`}
                    ></i>
                  </span>
                </div>

                {errors.password && (
                  <div className="login-popup-error">{errors.password}</div>
                )}
              </div>
              <div className="login-options-row">
                {/* REMEMBER ME */}

                <label className="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />

                  <span>Remember Me</span>
                </label>

                {/* FORGOT PASSWORD */}

                <span
                  className="login-popup-forgot"
                  onClick={() => {
                    setShowForgot(true);
                    setForgotStep(1);
                    setForgotIdentifier("");
                    setForgotOtp("");
                    setForgotEmail("");
                    setOtpTimer(0);
                  }}
                >
                  Forgot Password?
                </span>
              </div>

              {/* LOGIN BUTTON */}

              <button
                type="submit"
                className="login-popup-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing in..." : "Login"}
              </button>
            </form>

            {/* FOOTER */}

            <div className="login-popup-footer">
              <p>
                New here?{" "}
                <span
                  className="login-popup-link"
                  onClick={() => {
                    handleClose();
                    openRegister();
                  }}
                >
                  Create Account
                </span>
              </p>

              {/* <span
                className="login-popup-forgot"
                onClick={() => {
                  setShowForgot(true);
                  setForgotStep(1);
                  setForgotIdentifier("");
                  setForgotOtp("");
                  setForgotEmail("");
                  setOtpTimer(0);
                }}
              >
                Forgot Password?
              </span> */}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          FORGOT PASSWORD MODAL
      ========================================================= */}

      {showForgot && (
        <div className="login-popup-overlay">
          <div className="login-popup-card login-popup-layout p-1">
            {/* LEFT ADVERTISEMENT */}

            <div className="login-popup-ad">
              <AdsSquare />
            </div>

            {/* RIGHT CONTENT */}

            <div className="login-popup-content">
              <button
                className="login-popup-close"
                onClick={closeForgotPassword}
              >
                ✕
              </button>

              <h2 className="login-popup-title">Reset Password</h2>

              {/* =================================================
                  STEP 1
              ================================================= */}

              {forgotStep === 1 && (
                <form onSubmit={handleForgotPassword}>
                  <div className="login-popup-form-group">
                    <label>Email or Mobile Number</label>

                    <input
                      type="text"
                      className="login-popup-input"
                      placeholder="Enter registered email or mobile number"
                      value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value)}
                      autoComplete="username"
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-popup-btn"
                    disabled={forgotLoading}
                  >
                    {forgotLoading ? "Sending OTP..." : "Send OTP"}
                  </button>
                </form>
              )}

              {/* =================================================
                  STEP 2
              ================================================= */}

              {forgotStep === 2 && (
                <form onSubmit={handleForgotPassword}>
                  {/* OTP SENT MESSAGE */}

                  {forgotEmail && (
                    <div className="otp-sent-message">
                      <i className="bi bi-check-circle-fill"></i>

                      <div>
                        <strong>OTP sent successfully!</strong>

                        <span>
                          OTP has been sent to your registered email{" "}
                          <strong>{forgotEmail}</strong>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* OTP */}

                  <div className="login-popup-form-group">
                    <label>Enter OTP</label>

                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="login-popup-input"
                      placeholder="Enter 6 digit OTP"
                      value={forgotOtp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");

                        setForgotOtp(value);
                      }}
                    />

                    {/* TIMER */}

                    <div className="otp-timer-wrapper">
                      {otpTimer > 0 ? (
                        <span className="otp-timer">
                          <i className="bi bi-clock"></i>
                          Resend OTP in <strong>{formatOtpTimer()}</strong>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="otp-resend-btn"
                          onClick={handleResendOtp}
                          disabled={otpSending}
                        >
                          <i className="bi bi-arrow-repeat"></i>{" "}
                          {otpSending ? "Sending OTP..." : "Resend OTP"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* NEW PASSWORD */}

                  <div className="login-popup-form-group">
                    <label>New Password</label>

                    <div className="password-wrapper">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        className="login-popup-input"
                        placeholder="Enter new password"
                        value={formData.new_password || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            new_password: e.target.value,
                          }))
                        }
                      />

                      <span
                        className="password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        <i
                          className={`bi ${
                            showNewPassword ? "bi-eye-slash" : "bi-eye"
                          }`}
                        ></i>
                      </span>
                    </div>
                  </div>

                  {/* RESET BUTTON */}

                  <button
                    type="submit"
                    className="login-popup-btn"
                    disabled={forgotLoading}
                  >
                    {forgotLoading ? "Updating..." : "Reset Password"}
                  </button>

                  {/* CHANGE IDENTIFIER */}

                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "12px",
                    }}
                  >
                    <span
                      className="login-popup-link"
                      style={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setForgotStep(1);

                        setForgotOtp("");

                        setForgotEmail("");

                        setOtpTimer(0);
                      }}
                    >
                      Change Email / Mobile
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
